/**
 * Tiny fetch helper. The portal carries auth via the `sa_sid` cookie set by
 * the API on `.sportsacademygames.com`, so every request goes out with
 * `credentials: 'include'`. localStorage is never consulted for auth.
 */

const DEFAULT_API_BASE = "https://api.sportsacademygames.com";

export function getApiBaseUrl(): string {
  const fromEnv =
    typeof import.meta !== "undefined" && import.meta.env
      ? import.meta.env.VITE_API_BASE_URL
      : undefined;
  return (fromEnv ?? DEFAULT_API_BASE).replace(/\/$/, "");
}

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message: string) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

/**
 * Stable machine code for a caught request failure — the API's `error` body
 * field when present, else `http_<status>`, else `"unknown"` (network/other).
 * For analytics `reason` props and error branching; never user-facing copy.
 */
export function apiErrorCode(err: unknown): string {
  if (err instanceof ApiError) {
    const code = (err.body as { error?: string } | null)?.error;
    return typeof code === "string" && code ? code : `http_${err.status}`;
  }
  return "unknown";
}

/** One field-level complaint pulled off a `validation_error` response. */
export interface ApiValidationIssue {
  /** The offending field — `path[0]`, or `""` for a whole-body rule. */
  field: string;
  /**
   * Full path to the offending value, stringified. Depth matters: an array's
   * own cap reports `["topic_picks"]`, while a member's field reports
   * `["topic_picks", "3", "count"]` — a different limit needing different copy.
   */
  path: string[];
  /** Zod issue code, e.g. `"too_big"`, `"too_small"`, `"custom"`. */
  code: string;
  /** The cap that was exceeded, when the issue carries one. */
  maximum?: number;
  minimum?: number;
  /** The server's own message — a fallback, not customer-ready copy. */
  message?: string;
}

/**
 * First issue from a `400 { error: "validation_error", details: ZodIssue[] }`
 * body — the shape the API's global handler emits for any schema rejection
 * (see the ZodError branch in `packages/api/src/app.ts`).
 *
 * Exists so forms can name the field and the limit the user actually broke
 * instead of falling back to a generic "check the details" toast. The portal
 * has no zod dependency (see the note in `./validation.ts`), so `details` is
 * untyped wire data and every field is read defensively.
 *
 * Returns null for anything that isn't a validation error — a different API
 * error code, a network failure, or a malformed/empty `details`.
 */
export function apiValidationIssue(err: unknown): ApiValidationIssue | null {
  if (!(err instanceof ApiError)) return null;
  const body = err.body as { error?: string; details?: unknown } | null;
  if (body?.error !== "validation_error") return null;
  const first = Array.isArray(body.details) ? body.details[0] : null;
  if (!first || typeof first !== "object") return null;

  const issue = first as {
    code?: unknown;
    path?: unknown;
    maximum?: unknown;
    minimum?: unknown;
    message?: unknown;
  };
  // `path` is [] for a top-level `.refine()`, and its segments can be numbers
  // when the failing field sits inside an array.
  const path = Array.isArray(issue.path)
    ? issue.path.filter((seg) => seg != null).map((seg) => String(seg))
    : [];
  const out: ApiValidationIssue = {
    field: path[0] ?? "",
    path,
    code: typeof issue.code === "string" ? issue.code : "",
  };
  if (typeof issue.maximum === "number") out.maximum = issue.maximum;
  if (typeof issue.minimum === "number") out.minimum = issue.minimum;
  if (typeof issue.message === "string") out.message = issue.message;
  return out;
}

export interface ApiRequest {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
}

export async function apiFetch<T>(path: string, req: ApiRequest = {}): Promise<T> {
  const base = getApiBaseUrl();
  const init: RequestInit = {
    method: req.method ?? "GET",
    credentials: "include",
    headers: req.body ? { "content-type": "application/json" } : {},
  };
  if (req.body !== undefined) init.body = JSON.stringify(req.body);
  if (req.signal) init.signal = req.signal;

  const res = await fetch(`${base}${path}`, init);
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    body = null;
  }
  if (!res.ok) {
    const msg = (body as { error?: string } | null)?.error ?? `${res.status} ${res.statusText}`;
    throw new ApiError(res.status, body, msg);
  }
  return body as T;
}

/**
 * Clear the session cookie. Never throws — a caller trying to get OFF an account
 * shouldn't have to wrap this in a try — but it does report whether the server
 * actually confirmed, and callers must not assume it did.
 *
 * `POST /auth/logout` only clears the cookie (packages/api/src/routes/auth.ts),
 * so a request that never lands leaves the previous identity fully signed in. Any
 * caller whose next step depends on being signed OUT has to verify that, not
 * trust this boolean and certainly not ignore it: re-read `/me` and check.
 * Invalidate ['me'] afterwards either way.
 */
export async function logout(): Promise<boolean> {
  try {
    await apiFetch("/auth/logout", { method: "POST" });
    return true;
  } catch {
    return false;
  }
}
